/* AFFLICTIONS (LS-224; Vijay: "we need more afflictions than just heat
 * wetness growth"). An affliction is a part on a body with a time left and
 * an effect while it lasts: put there by the field the body stands in or the
 * thing it touches, ticked each frame, gone when its time runs out. The
 * body's own rules read what it has -- speed reads Soaked, fire reads
 * Soaked, a creature's sight reads Blinded -- and never who put it there. */
import { wearing } from './inventory.ts';

export type Affliction = { name: string; left: number; data?: any; t?: number };

export function afflict(body: any, name: string, frames: number, data: any = null) {
  if (!body.afflictions) body.afflictions = new Map<string, Affliction>();
  const had = body.afflictions.get(name);
  if (had) { had.left = Math.max(had.left, frames); if (data) had.data = data; return had; }
  const a = { name, left: frames, data }; body.afflictions.set(name, a); return a;
}
export const afflicted = (body: any, name: string) => !!(body && body.afflictions && body.afflictions.get(name) && body.afflictions.get(name).left > 0);
/** One frame for every affliction a body carries; the effects that need a frame's work happen here. */
/** THE BEAT AN AFFLICTION TAKES A POINT ON, and how soon the first one comes.
 *
 *  THE FIRST POINT USED TO BE UP TO SIX SECONDS AFTER THE CAUSE (2026-09-10;
 *  Vijay: "I just spawned into a world and started losing health, and I didn't
 *  understand why. Turns out I was scorched, and I didn't realize that being
 *  scorched means you lose health over time."). A scorching takes a point
 *  every 360 frames and the clock started when the ground caught you, so the
 *  vigor went six seconds after the step that cost it -- long enough to have
 *  walked on, fought something, and lost the connection entirely. Cause and
 *  effect have to be close enough to be ONE event, so every affliction that
 *  takes vigor takes its first point within three quarters of a second and
 *  then settles into its own slow beat. */
export const FIRST_BITE = 45;
export function tickAfflictions(game: any, body: any) {
  if (!body || !body.afflictions) return;
  if (body.ailFlash && body.ailFlash.t > 0) body.ailFlash.t--;   // the fifth of a second the HUD lights up for
  for (const [name, a] of body.afflictions) {
    /* THE BONE WARD (LS-335): what is on the PLAYER runs out twice as fast --
     * a bleed, a poison, a chill, a scorching, smoke. It is the player's charm,
     * so it never hurries a creature's afflictions. */
    a.left -= (game && game.player && body === game.player && wearing(game.state, 'ward')) ? 2 : 1;
    // its own clock: `left` is refreshed every frame you stand in smoke, so a beat keyed to it never came round
    a.t = (a.t || 0) + 1;
    /* ...AND THE BODY REMEMBERS WHAT TOOK IT, for a fifth of a second, so the
     * HUD can flash the vigor and the name of the thing with one colour on
     * one frame. A player who sees a leaf go and a word light up together has
     * been told what is happening to them without a line of text. */
    const bites = (aa: any, beat: number) => {
      if (aa.bit === undefined) aa.bit = 0;
      const due = aa.bit === 0 ? FIRST_BITE : FIRST_BITE + aa.bit * beat;
      if (aa.t < due) return false;
      if (body === (game && game.player) && body.invuln > 0) return false;   // the bite waits for the hit's own grace to close (2026-09-19): it used to be spent inside it, and the HUD flashed a point nobody lost
      aa.bit++;
      if (body === (game && game.player)) body.ailFlash = { name, t: 12 };
      return true;
    };
    // ...and it is heard, like the smoke and the wound and the scorched ground (the music lens, round 50)
    if (name === 'Poisoned' && bites(a, 90) && body.hurt) { body.hurt(game, 1, body.cx, body.cy + 8, 'poison'); if (game.sfx) game.sfx('poison', body.x); if (game.puff) game.puff(body.cx, body.cy - 6, '#9fd08a', 3); }
    // LS-224: smoke chokes (a point every two seconds while you stand in it), a tusk wound bleeds (a point every four seconds until sap)
    if (name === 'Choking' && bites(a, 120) && body.hurt) { body.hurt(game, 1, body.cx, body.cy + 8, 'smoke'); if (game.sfx) game.sfx('choke', body.x); if (game.puff) for (let i = 0; i < 3; i++) game.puff(body.cx + (i - 1) * 3, body.cy - 10, '#8a8494', 3); }
    if (name === 'Bleeding' && bites(a, 240) && body.hurt) { body.hurt(game, 1, body.cx, body.cy + 8, 'bleed'); if (game.sfx) game.sfx('bleed', body.x); if (game.puff) game.puff(body.cx, body.cy + 4, '#a8323a', 3); }
    if (name === 'Spored' && a.t % 30 === 0 && game.puff) game.puff(body.cx + (a.t % 60 ? 3 : -3), body.cy - 8, '#c8b8e0', 2);
    // LS-240: scorched ground takes a point every six seconds while you stand on it, with a shimmer of sparks
    // ...and it is HEARD on the beat it hurts, the way smoke and a tusk wound are (the music lens, round 49: the newest thing that takes a point off you made no sound at all)
    if (name === 'Scorched' && bites(a, 360) && body.hurt) { body.hurt(game, 1, body.cx, body.cy + 8, 'heat'); if (game.sfx) game.sfx('burn', body.x); if (game.puff) game.puff(body.cx, body.cy - 6, '#ffb85c', 4, 'spark'); }
    if (name === 'Scorched' && a.t % 20 === 0 && game.puff) game.puff(body.cx + (a.t % 40 ? 4 : -4), body.cy - 10, '#ffe08a', 1, 'spark');
    if (a.left <= 0) body.afflictions.delete(name);
  }
}
/** What a body's afflictions do to its speed: soaked or chilled, it is slower. */
/** The names on the bar, in the order they matter. */
export const AFFLICTION_NAMES = ['Bleeding', 'Poisoned', 'Choking', 'Spored', 'Chilled', 'Soaked', 'Scorched'];   // Scorched: the ground too hot to stand on (LS-240)
export function activeNames(body: any): string[] { return body && body.afflictions ? AFFLICTION_NAMES.filter((n) => afflicted(body, n)) : []; }
export function cure(body: any, name: string) { if (body && body.afflictions) body.afflictions.delete(name); }
export function speedMul(body: any) {
  let m = 1;
  /* ONE SLOWDOWN (LS-305, decided 2026-09-13): Soaked, Chilled and Spored all meant "you are slower", at three rates that
   * stacked to half speed with three different clearing rules. They are one rule now: any of them, a fifth off, once;
   * each keeps its own name and its own cure (the hide and a fire for the cold, the dry for the wet, time for the spores). */
  if (afflicted(body, 'Soaked') || afflicted(body, 'Chilled') || afflicted(body, 'Spored')) m *= 0.8;
  if (afflicted(body, 'Choking')) m *= 0.9;
  return m;
}
